/**
 * Mock Category Repository
 *
 * 로컬 Mock 데이터를 사용하여 카테고리 데이터를 제공합니다.
 */

import type { Category } from '../types/Category'
import type { CategoryRepository } from './CategoryRepository'
import { allCategories } from '../mocks/mockCategories'

/**
 * Mock Category Repository 구현
 */
export class MockCategoryRepository implements CategoryRepository {
  /**
   * 내부 데이터 저장소 (CRUD 작업 가능하도록 mutable)
   * 대분류만 저장 (children에 소분류 포함)
   */
  private categories: Category[] = JSON.parse(JSON.stringify(allCategories))

  /**
   * 지연 시뮬레이션 (네트워크 요청 흉내)
   */
  private delay(ms: number = 50): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  /**
   * ID 생성
   */
  private generateId(): string {
    return `cat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * 모든 카테고리 조회
   */
  async findAll(): Promise<Category[]> {
    await this.delay()
    // 깊은 복사 (children 포함)
    return JSON.parse(JSON.stringify(this.categories))
  }

  /**
   * 타입별 카테고리 조회
   */
  async findByType(type: 'income' | 'expense'): Promise<Category[]> {
    await this.delay()
    const filtered = this.categories.filter((c) => c.type === type)
    return JSON.parse(JSON.stringify(filtered))
  }

  /**
   * ID로 카테고리 조회
   */
  async findById(id: string): Promise<Category | null> {
    await this.delay()
    // 대분류에서 찾기
    for (const major of this.categories) {
      if (major.id === id) {
        return JSON.parse(JSON.stringify(major))
      }

      // 소분류에서 찾기
      if (major.children) {
        const minor = major.children.find((c) => c.id === id)
        if (minor) {
          return JSON.parse(JSON.stringify(minor))
        }
      }
    }

    return null
  }

  /**
   * 새 카테고리 생성
   */
  async create(input: Omit<Category, 'id'>): Promise<Category> {
    await this.delay()
    const newCategory: Category = {
      id: this.generateId(),
      ...input,
    }

    // 대분류인 경우 (parentId 없음)
    if (!input.parentId) {
      this.categories.push(newCategory)
    } else {
      // 소분류인 경우 - 부모 카테고리 찾아서 children에 추가
      const parent = this.categories.find((c) => c.id === input.parentId)
      if (!parent) {
        throw new Error(`부모 카테고리를 찾을 수 없습니다: ${input.parentId}`)
      }
      if (!parent.children) {
        parent.children = []
      }
      parent.children.push(newCategory)
    }

    return JSON.parse(JSON.stringify(newCategory))
  }

  /**
   * 카테고리 수정
   */
  async update(id: string, input: Partial<Category>): Promise<Category> {
    await this.delay()

    // 대분류에서 찾기
    const majorIndex = this.categories.findIndex((c) => c.id === id)
    if (majorIndex !== -1) {
      const updated = { ...this.categories[majorIndex], ...input, id } as Category
      this.categories[majorIndex] = updated
      return JSON.parse(JSON.stringify(updated))
    }

    // 소분류에서 찾기
    for (const major of this.categories) {
      if (major.children) {
        const minorIndex = major.children.findIndex((c) => c.id === id)
        if (minorIndex !== -1) {
          const updated = { ...major.children[minorIndex], ...input, id } as Category
          major.children[minorIndex] = updated
          return JSON.parse(JSON.stringify(updated))
        }
      }
    }

    throw new Error(`카테고리를 찾을 수 없습니다: ${id}`)
  }

  /**
   * 카테고리 삭제
   */
  async delete(id: string): Promise<void> {
    await this.delay()

    // 대분류에서 찾기
    const majorIndex = this.categories.findIndex((c) => c.id === id)
    if (majorIndex !== -1) {
      this.categories.splice(majorIndex, 1)
      return
    }

    // 소분류에서 찾기
    for (const major of this.categories) {
      if (major.children) {
        const minorIndex = major.children.findIndex((c) => c.id === id)
        if (minorIndex !== -1) {
          major.children.splice(minorIndex, 1)
          return
        }
      }
    }

    throw new Error(`카테고리를 찾을 수 없습니다: ${id}`)
  }
}
